import { Close } from '@mui/icons-material';
import {
  Typography,
  Menu as MuiMenu,
  MenuItem as MuiMenuItem,
  CircularProgress,
  useMediaQuery,
  MenuProps,
  Stack,
  IconButton,
  MenuItemProps,
} from '@mui/material';
import { MouseEvent, ReactNode } from 'react';

import { ResponsiveMenuProvider, useResponsiveMenuContext } from '@/context/ResponsiveMenuProvider';

export interface ResponsiveMenuProps
  extends Omit<MenuProps, 'anchorEl' | 'open' | 'onClose'>,
    Required<Pick<MenuProps, 'anchorEl' | 'open'>> {
  onClose: () => void;
  mobileTitle?: string;
  children: ReactNode;
}

export const ResponsiveMenu = ({
  anchorEl,
  open,
  onClose,
  mobileTitle,
  children,
  ...rest
}: ResponsiveMenuProps) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));

  return (
    <ResponsiveMenuProvider isOpen={open} onClose={onClose}>
      {isMobile ? (
        <MuiMenu
          anchorEl={anchorEl}
          open={open}
          onClose={onClose}
          className="MuiMenu-mobile"
          marginThreshold={0}
          {...rest}
          slotProps={{
            paper: {
              sx: {
                maxWidth: '100%',
                maxHeight: '100%',
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                borderRadius: 3,
                backgroundColor: 'black',
                gap: 3,
              },
            },
          }}>
          <ResponsiveMenuItem
            className="MuiMenu-mobileTitle"
            disableRipple
            disableCloseOnClick
            sx={{ borderBottomWidth: 0 }}>
            <Stack width="100%" direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="body1" color="text.secondary" component="span">
                {mobileTitle ?? ''}
              </Typography>
              <IconButton onClick={onClose} edge="end" sx={{ color: 'text.secondary' }}>
                <Close />
              </IconButton>
            </Stack>
          </ResponsiveMenuItem>
          {children}
        </MuiMenu>
      ) : (
        <MuiMenu
          anchorEl={anchorEl}
          open={open}
          onClose={onClose}
          {...rest}
          slotProps={{
            paper: {
              sx: {
                width: 'auto',
                minWidth: 280,
                borderRadius: 3,
                ml: -4.5,
              },
            },
          }}>
          {children}
        </MuiMenu>
      )}
    </ResponsiveMenuProvider>
  );
};

export const ResponsiveMenuItem = ({
  isLoading,
  disableCloseOnClick,
  onClick: externalOnClick,
  children,
  ...rest
}: MenuItemProps & {
  isLoading?: boolean;
  disableCloseOnClick?: boolean;
}) => {
  const { onClose } = useResponsiveMenuContext();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const onClick = (event: MouseEvent<HTMLLIElement>) => {
    if (!disableCloseOnClick) {
      onClose();
    }
    externalOnClick?.(event);
  };

  return (
    <MuiMenuItem {...rest} onClick={onClick}>
      {typeof children === 'string' || typeof children === 'number' ? (
        <Typography variant={isMobile ? 'h5' : 'h6'} component="span" noWrap>
          {children}
        </Typography>
      ) : (
        children
      )}
      {isLoading && <CircularProgress size="1em" sx={{ ml: '0.5em' }} />}
    </MuiMenuItem>
  );
};
